import { REGEX, validateInput } from 'functions/validationUtils';
import { Steps } from 'types/signUpForm';
import { trimInputValue } from 'functions/trimInputValue';
import { useAppSettings } from 'context/appsettingsContext';
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import IntercomScript from 'components/IntercomScript/IntercomScript';
import StepDetails from './SignUpSteps/StepDetails';
import StepEmail from './SignUpSteps/StepEmail';
import StepPassword from './SignUpSteps/StepPassword';
import { Turnstile } from '@marsidev/react-turnstile';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import Submitted from './SignUpSteps/Submitted';

export default function SignUpForm() {
  const ref = useRef<TurnstileInstance>(null);
  const settings = useAppSettings();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [currentStep, setCurrentStep] = useState('email');
  
  // Determine region based on firebase project ID
  const isRSARegion = settings.firebaseProjectId.toLowerCase().endsWith('rsa') || settings.firebaseProjectId.toLowerCase().endsWith('rsa-stg');
  const isUSARegion = settings.firebaseProjectId.toLowerCase().endsWith('usa') || settings.firebaseProjectId.toLowerCase().endsWith('usa-stg');

  const [formData, setFormData] = useState({
    AcceptedTermsAndConditionsAndPolicy: false,
    AcceptedToAllowInformationToBeUsedToContact: false,
    Email: '',
    FirstName: '',
    IdNumber: '',
    IdNumberIsSouthAfrican: isRSARegion,
    LastName: '',
    Password: '',
    PhoneCode: isRSARegion ? '27' : isUSARegion ? '1' : /*isUKRegion*/ '44',
    PhoneNumber: '',
    turnstileToken: '',
  });

  const [formErrors, setFormErrors] = useState({
    Email: [],
    AcceptedTermsAndConditionsAndPolicy: [],
    AcceptedToAllowInformationToBeUsedToContact: [],
    Password: [],
    FirstName: [],
    LastName: [],
    PhoneNumber: [],
    IdNumber: [],
    PhoneCode: [],
  });

  useEffect(() => {
    document.title = 'Sign up | Titan Secure';
  });

  async function handleUserSignUp() {
    sessionStorage.setItem(
      'userPhoneNumber',
      JSON.stringify({
        PhoneNumber: formData.PhoneNumber,
        PhoneCode: formData.PhoneCode,
      })
    );

    setSubmitError(false);
    setIsSubmitting(true);

    try {
      
      const response = await axios.post(settings.sendEmailHandlerUrl, {
        emailType: 'new_user',
        content: {
          fromName: `${formData.FirstName} ${formData.LastName}`,
          email: formData.Email,
          firstName: formData.FirstName,
          lastName: formData.LastName,
          phoneNumber: `(+${formData.PhoneCode}) ${formData.PhoneNumber}`,
          idNumber: formData.IdNumber,
          idNumberIsSouthAfrican: formData.IdNumberIsSouthAfrican,
        }
      });
      
      if (response?.status === 200) {
        setCurrentStep('submitted');
      } else {
        setSubmitError(true);
        console.error(response);
      }

    } catch (error: any) {

      setSubmitError(true);
      console.error(error);

      /*
      if (axios.isAxiosError(error)) {
        
        const errors = error?.response?.data?.errors;

        if (errors) {
          setFormErrors(errors);
          if (errors?.Email) {
            setCurrentStep('email');
          } else if (errors?.Password) {
            setCurrentStep('Password');
          } else {
            setCurrentStep('details');
          }
        }
      }
      */
    }

    setIsSubmitting(false);
  }

  /**
   * Handle validations
   */
  function handleInputValidation(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setFormErrors((prevState) => ({
      ...prevState,
      [id]: validateInput(id, value, isRSARegion, isUSARegion, false),
    }));
  }

  /**
   * Handle changes on inputs
   */
  function handleOnChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value, type, checked } = e.target;

    const inputValue = trimInputValue(id, value);

    if (id === 'PhoneNumber') {
      if (REGEX.Numbers.test(inputValue) || !inputValue) {
        setFormData((prevState) => ({ ...prevState, [id]: inputValue }));
      }
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [id]: type === 'checkbox' ? checked : inputValue,
      }));
    }

    if (id === 'IdNumberIsSouthAfrican') {
      handleIdentityReset();
    }
  }

  /**
   * Reset IdNumber field and errors
   */
  function handleIdentityReset() {
    setFormData((prevState) => ({
      ...prevState,
      IdNumber: '',
    }));

    setFormErrors((prevState) => ({
      ...prevState,
      IdNumber: [],
    }));
  }

  /**
   * Next step
   */
  function handleStepChange(step: Steps) {
    if (step === 'password') {
      step = 'details';
    }
    setCurrentStep(step);
  }

  function setTurnstileToken(token: string) {
    setFormData((prevState) => ({ ...prevState, turnstileToken: token }));
  }

  const formSteps = () => {
    switch (currentStep) {
      case 'email':
        return (
          <StepEmail
            handleStepChange={handleStepChange}
            formData={formData}
            handleOnChange={handleOnChange}
            formErrors={formErrors}
            handleInputValidation={handleInputValidation}
          />
        );
      case 'password':
        return (
          <StepPassword
            handleStepChange={handleStepChange}
            formData={formData}
            handleOnChange={handleOnChange}
            formErrors={formErrors}
            handleInputValidation={handleInputValidation}
          />
        );
      case 'details':
        return (
          <StepDetails
            handleStepChange={handleStepChange}
            formData={formData}
            handleOnChange={handleOnChange}
            formErrors={formErrors}
            handleInputValidation={handleInputValidation}
            handleSubmit={handleUserSignUp}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        );
      case 'submitted':
          return (
            <Submitted/>
          );
    }
  };

  return (
    <>
      {formSteps()}
      <IntercomScript />
      {`${settings.turnstileSiteEnabled}`.toLowerCase() === 'true' && settings.turnstileSiteKey && (
        <Turnstile
          siteKey={settings.turnstileSiteKey}
          options={{ size: 'invisible' }}
          ref={ref}
          onSuccess={setTurnstileToken}
        />
      )}
    </>
  );
}
